import { Filter, SavedSearch } from '../../../../../../components/ProfileArea/types';
import {
  filterColumnFilters,
  determineSearchType,
} from '../../../../../../components/ProfileArea/favoritesUtils';
import {
  encodeData,
  encodeFiltersToParams,
  removePanelFiltersFromSearchParams,
} from '../../../../../../utils/encodeUtils';
import { getFilters } from '../../../../../../services/savedFiltersService';
import { DATASETS } from '../../../../../../components/Constants';

function resolveSavedSearchIsEvidence(search: SavedSearch): boolean {
  if (search.searchContext?.isEvidence !== undefined) {
    return Boolean(search.searchContext.isEvidence);
  }
  return search.table === 'Evidence';
}

function applyColumnFiltersToUrl(url: URL, columnFilters: Filter[], fallbackIsEvidence: boolean) {
  if (columnFilters.length === 0) return;

  const filtersWithPanel = columnFilters.map((f: Filter) => ({
    ...f,
    isEvidence: f.isEvidence !== undefined ? Boolean(f.isEvidence) : fallbackIsEvidence,
    operator: f.operator ?? 'eq',
    type: f.type ?? 'string',
  }));

  removePanelFiltersFromSearchParams(url.searchParams, 'evidence');
  removePanelFiltersFromSearchParams(url.searchParams, 'summary');
  encodeFiltersToParams(filtersWithPanel).forEach((value, key) => {
    url.searchParams.set(key, value);
  });
  url.searchParams.set('f', encodeData(filtersWithPanel));
}

function applyActivePanelToUrl(url: URL, search: SavedSearch) {
  if (search.searchContext?.summaryMatch) {
    url.searchParams.set('activePanel', 'summary');
    url.searchParams.delete('isEvidence');
    return;
  }
  if (search.searchContext?.isEvidence === true) {
    url.searchParams.set('activePanel', 'evidence');
    return;
  }
  if (search.searchContext?.isEvidence === false) {
    url.searchParams.set('activePanel', 'summary');
    return;
  }
  if (!url.searchParams.get('activePanel')) {
    url.searchParams.set('activePanel', resolveSavedSearchIsEvidence(search) ? 'evidence' : 'summary');
  }
}
export function loadSavedSearches(): Promise<SavedSearch[]> {
  const cloudPromise = getFilters()
    .then(response => {
      const cloudFilters = response.filters || [];
      return cloudFilters.map((filter: any) => {
        const isEvidence =
          filter.searchContext?.isEvidence !== undefined
            ? Boolean(filter.searchContext.isEvidence)
            : Boolean(filter.isEvidence);
        return {
          id: filter.filterId,
          table: filter.type === 'score' ? 'Score' : isEvidence ? 'Evidence' : 'Summary',
          filters: filter.filters,
          searchQuery: filter.searchQuery,
          savedAt: filter.createdAt,
          name: filter.name,
          type: determineSearchType(filter),
          columnOrder: filter.columnOrder,
          columnVisibilityModel: filter.columnVisibilityModel,
          searchContext: filter.searchContext,
          sourceWeights: filter.sourceWeights,
          isLocal: false,
        };
      });
    })
    .catch(() => []);

  const localColumnSelections: SavedSearch[] = [];
  const localScores: SavedSearch[] = [];
  Object.keys(localStorage).forEach((key: string) => {
    if (key.startsWith('columnSelection_') || key.startsWith('completeSearch_')) {
      try {
        const selection = JSON.parse(localStorage.getItem(key) || '');
        localColumnSelections.push({
          id: key,
          table: selection.searchContext?.isEvidence ? 'Evidence' : 'Summary',
          filters: selection.columnFilters || selection.columnVisibilityModel || [],
          searchQuery: selection.searchQuery || null,
          savedAt: selection.savedAt,
          name: selection.name,
          type: key.startsWith('completeSearch_') ? 'complete' : 'columns',
          columnOrder: selection.columnOrder,
          columnVisibilityModel: selection.columnVisibilityModel,
          searchContext: selection.searchContext,
          isLocal: true,
        });
      } catch {
        // ignore
      }
    }
    if (key.startsWith('savedScore_')) {
      try {
        const scoreData = JSON.parse(localStorage.getItem(key) || '');
        localScores.push({
          id: key,
          table: 'Score',
          filters: [],
          searchQuery: null,
          savedAt: scoreData.savedAt,
          name: scoreData.name,
          type: 'score',
          sourceWeights: scoreData.sourceWeights,
          searchContext: scoreData.searchContext,
          isLocal: true,
        });
      } catch {
        // ignore
      }
    }
  });

  return cloudPromise.then(searches => {
    const all = [...searches, ...localColumnSelections, ...localScores];
    return all.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());
  });
}

export function buildSearchUrl(search: SavedSearch): string | null {
  try {
    const url = new URL(`${window.location.origin}/search`);
    const filters = search.filters || [];
    const fallbackIsEvidence = resolveSavedSearchIsEvidence(search);

    const getColumnFiltersForUrl = (): Filter[] => {
      const column = filters.filter(
        (f: Filter) =>
          !['view', 'tab', 'source', 'idents'].includes(f.name) &&
          f.value !== undefined &&
          f.value !== null &&
          f.value !== '',
      );
      return column.length > 0 ? column : filterColumnFilters(filters);
    };

    if (search.searchQuery) {
      const searchParams = new URLSearchParams(search.searchQuery);
      searchParams.forEach((value, key) => url.searchParams.set(key, value));
      const columnFilters = getColumnFiltersForUrl();
      applyColumnFiltersToUrl(url, columnFilters, fallbackIsEvidence);
      applyActivePanelToUrl(url, search);
      return url.href;
    }

    if (filters.length === 0) return null;

    const getFilterValue = (name: string, defaultValue: string) =>
      filters.find((f: Filter) => f.name === name)?.value || defaultValue;

    url.searchParams.set('view', getFilterValue('view', 'ALL'));
    url.searchParams.set('tab', getFilterValue('tab', 'GDA'));
    url.searchParams.set('source', getFilterValue('source', 'ALL'));
    url.searchParams.set('idents', getFilterValue('idents', 'ALL'));

    const columnFilters = getColumnFiltersForUrl();
    applyColumnFiltersToUrl(url, columnFilters, fallbackIsEvidence);
    applyActivePanelToUrl(url, search);

    return url.href;
  } catch {
    return null;
  }
}

export function normalizeDataset(raw?: string) {
  const normalized = String(raw ?? '')
    .trim()
    .toUpperCase();
  if (normalized === DATASETS.DISEASES || normalized === 'DISEASE') return DATASETS.DISEASES;
  if (normalized === DATASETS.GENES || normalized === 'GENE') return DATASETS.GENES;
  if (normalized === DATASETS.VARIANTS || normalized === 'VARIANT') return DATASETS.VARIANTS;
  if (normalized === DATASETS.CHEMICALS || normalized === 'CHEMICAL') return DATASETS.CHEMICALS;
  return undefined;
}

export function getSearchPlaceholderAndSuggestions(activeSearchDataset?: string) {
  const searchPlaceholder =
    activeSearchDataset === DATASETS.DISEASES
      ? 'Add disease...'
      : activeSearchDataset === DATASETS.GENES
        ? 'Add gene...'
        : activeSearchDataset === DATASETS.VARIANTS
          ? 'Add variant...'
          : activeSearchDataset === DATASETS.CHEMICALS
            ? 'Add chemical...'
            : 'Add gene, disease, variant...';

  const searchSuggestions =
    activeSearchDataset === DATASETS.DISEASES
      ? ['Obesity', 'Alzheimer', 'Parkinson']
      : activeSearchDataset === DATASETS.GENES
        ? ['BRCA1', 'LEPR', 'TP53']
        : activeSearchDataset === DATASETS.VARIANTS
          ? ['rs121913279', 'rs28934578', 'rs113488022']
          : activeSearchDataset === DATASETS.CHEMICALS
            ? ['Aspirin', 'Ibuprofen', 'Caffeine']
            : ['Obesity', 'BRCA1', 'rs121913279', 'Aspirin'];

  return { searchPlaceholder, searchSuggestions };
}

export function getFavoriteSubtitle(search: SavedSearch): string {
  if (search.type === 'score') {
    const sourcesCount = search.sourceWeights?.length ?? 0;
    return `${sourcesCount} source${sourcesCount === 1 ? '' : 's'} configured`;
  }
  if (search.type === 'columns') {
    const visibleCount = Object.values(search.columnVisibilityModel ?? {}).filter(Boolean).length;
    return `${visibleCount} column${visibleCount === 1 ? '' : 's'} visible`;
  }
  if (search.type === 'complete') {
    const match = search.searchContext?.summaryMatch?.stats;
    if (match?.matchFilterCount != null && match.matchFilterLabel) {
      return `Summary match · ${match.matchFilterCount} ${match.matchFilterLabel}`;
    }
    return 'Saved full search';
  }
  const contextSource = search.searchContext?.source;
  return contextSource ? `Source: ${contextSource}` : 'Saved search';
}
